// components/leads-clients/lead-client/lead-client.component.jsx
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";

import RetroButton from "../../retro/button/retro-button.component";
import Avatar from "../../retro/avatar/avatar.component";
import ItemAddress from "../../item-address/ItemAddress";
import DropdownMenu from "../../dropdown-menu/DropdownMenu";
import SearchPeople from "../../socials/profile/SearchPeople";
import InvoicesList from "../../billings/invoices-list.component";

import { db, fieldValue } from "../../../firebase/firebase.utils";

import InteractiveHeatmap from "../../interactive-heatmap/InteractiveHeatmap";

import { BsPlusLg } from "react-icons/bs";
import { TfiLink, TfiUnlink } from "react-icons/tfi";

import {
  TbAffiliate,
  TbBrandFacebook,
  TbBrandInstagram,
  TbBrandLinkedin,
  TbUserCheck,
  TbUserPlus,
  TbUserX,
  TbWorldWww,
} from "react-icons/tb";

import {
  FiChevronDown,
  FiChevronRight,
  FiExternalLink,
  FiPlus,
} from "react-icons/fi";

import "./lead-client.styles.scss";

const PLATFORM_OPTIONS = [
  { value: "Antlogy", Icon: TbWorldWww },
  { value: "LinkedIn", Icon: TbBrandLinkedin },
  { value: "Facebook", Icon: TbBrandFacebook },
  { value: "Instagram", Icon: TbBrandInstagram },
  { value: "Web", Icon: TbWorldWww },
  { value: "Client Referral", Icon: TbUserCheck },
  { value: "Friend Referral", Icon: TbUserPlus },
  { value: "Former Client", Icon: TbUserX },
  { value: "Affiliate", Icon: TbAffiliate },
];

const TYPE_OPTIONS = [
  { key: "Lead", label: "Lead" },
  { key: "Client", label: "Client" },
];

const getProjectId = (project) => project?.stationId || project?.id;

const externalHref = (value) => {
  if (!value) return "";
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

function SectionHeader({ title, open, onClick }) {
  return (
    <button type="button" className="client__section-toggle" onClick={onClick}>
      <h3>{title}</h3>
      {open ? <FiChevronDown /> : <FiChevronRight />}
    </button>
  );
}

export default function Client({
  currentUser,
  client,
  clientName,
  setClientName,
  handleCloseClient,
  handleSaveClient,
  handleDeleteClient,
  clientmodalMode,
  clientPlatform,
  setClientPlatform,
  clientProjects,
  setClientProjects,
  clientEmail,
  setClientEmail,
  clientPhone,
  setClientPhone,
  referredBy,
  setReferredBy,
  type,
  setType,
  clientSocialLink,
  setClientSocialLink,
  clientWebLink,
  setClientWebLink,
  clientNotes,
  setClientNotes,
  currentSpaceId,
}) {
  const stationData = useSelector((state) => state.space.stationData) || [];

  const [targetSpaceId, setTargetSpaceId] = useState(currentSpaceId);

  const [clientHistory, setClientHistory] = useState([]);
  const [people, setPeople] = useState([]);
  const [linkedPeople, setLinkedPeople] = useState([]);

  const [showBillings, setShowBillings] = useState(true);
  const [showLinkedProjects, setShowLinkedProjects] = useState(true);
  const [showHistory, setShowHistory] = useState(true);
  const [showContactInfo, setShowContactInfo] = useState(true);

  const [linkProjectIsOpen, setLinkProjectIsOpen] = useState(false);
  const [linkPersonIsOpen, setLinkPersonIsOpen] = useState(false);
  const [showLinkedPeople, setShowLinkedPeople] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const selectedPlatform =
    PLATFORM_OPTIONS.find((item) => item.value === clientPlatform) ||
    PLATFORM_OPTIONS[0];

  const SelectedPlatformIcon = selectedPlatform.Icon;

  const platformItems = PLATFORM_OPTIONS.map(({ value, Icon }) => ({
    key: value,
    label: value,
    icon: <Icon />,
  }));

  const availableProjects = useMemo(
    () =>
      stationData
        .filter((project) => project.fromSpaceId === currentSpaceId)
        .slice()
        .sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", undefined, {
            numeric: true,
            sensitivity: "base",
          }),
        ),
    [stationData, currentSpaceId],
  );

  const linkedProjects = useMemo(
    () =>
      availableProjects.filter((project) =>
        clientProjects.includes(getProjectId(project)),
      ),
    [availableProjects, clientProjects],
  );

  useEffect(() => {
    setTargetSpaceId(currentSpaceId);
  }, [currentSpaceId]);

  useEffect(() => {
    if (clientmodalMode !== "edit" || !currentSpaceId || !client?.id) {
      setClientHistory([]);
      setPeople([]);
      return;
    }

    const unsubscribe = db
      .collection("space")
      .doc(currentSpaceId)
      .collection("clients")
      .doc(client.id)
      .onSnapshot((snapshot) => {
        if (!snapshot.exists) return;

        const data = snapshot.data();
        const history = Array.isArray(data.history) ? data.history : [];

        setClientHistory(
          history.slice().sort((a, b) => (a.date > b.date ? 1 : -1)),
        );

        setPeople(Array.isArray(data.people) ? data.people : []);

        if (Array.isArray(data.projects)) {
          setClientProjects(data.projects);
        }
      });

    return unsubscribe;
  }, [currentSpaceId, client?.id, clientmodalMode, setClientProjects]);

  useEffect(() => {
    let active = true;

    if (!people.length) {
      setLinkedPeople([]);
      return;
    }

    Promise.all(
      people.map((uid) =>
        db
          .collection("users")
          .doc(uid)
          .get()
          .then((snapshot) =>
            snapshot.exists
              ? {
                  ...snapshot.data(),
                  uid: snapshot.id,
                }
              : null,
          ),
      ),
    )
      .then((users) => {
        if (active) {
          setLinkedPeople(users.filter(Boolean));
        }
      })
      .catch((error) => {
        console.log("Linked people load error:", error);
      });

    return () => {
      active = false;
    };
  }, [people]);

  const openNewInvoice = () => {
    if (!client?.id) return;

    navigate(
      `/s/${currentSpaceId}/billings/invoices/new?clientId=${encodeURIComponent(
        client.id,
      )}`,
      {
        state: {
          backgroundPath: location.pathname + location.search,
        },
      },
    );
  };

  const openExistingInvoice = (invoice) => {
    navigate(`/s/${currentSpaceId}/billings/invoices/${invoice.id}`, {
      state: {
        backgroundPath: location.pathname + location.search,
      },
    });
  };

  const openProjectPicker = () => {
    setLinkPersonIsOpen(false);
    setShowLinkedPeople(false);
    setLinkProjectIsOpen((current) => !current);
  };

  const openPersonPicker = () => {
    if (clientmodalMode !== "edit") return;

    setLinkProjectIsOpen(false);
    setShowLinkedPeople(false);
    setLinkPersonIsOpen((current) => !current);
  };

  const openLinkedPeople = () => {
    if (!people.length) return;

    setLinkProjectIsOpen(false);
    setLinkPersonIsOpen(false);
    setShowLinkedPeople((current) => !current);
  };

  const handleLinkProject = async (projectId) => {
    if (!projectId || clientProjects.includes(projectId)) {
      setLinkProjectIsOpen(false);
      return;
    }

    try {
      if (clientmodalMode === "edit" && client?.id) {
        await db
          .collection("space")
          .doc(currentSpaceId)
          .collection("clients")
          .doc(client.id)
          .update({
            projects: fieldValue.arrayUnion(projectId),
          });
      }

      setClientProjects((current) => [...current, projectId]);
      setShowLinkedProjects(true);
      setLinkProjectIsOpen(false);
    } catch (error) {
      console.error("Link project error:", error);
    }
  };

  const handleUnlinkProject = async (projectId) => {
    try {
      if (clientmodalMode === "edit" && client?.id) {
        await db
          .collection("space")
          .doc(currentSpaceId)
          .collection("clients")
          .doc(client.id)
          .update({
            projects: fieldValue.arrayRemove(projectId),
          });
      }

      setClientProjects((current) => current.filter((id) => id !== projectId));
    } catch (error) {
      console.error("Unlink project error:", error);
    }
  };

  const handleLinkPerson = async (person) => {
    if (
      clientmodalMode !== "edit" ||
      !client?.id ||
      !person?.uid ||
      people.includes(person.uid)
    ) {
      setLinkPersonIsOpen(false);
      return;
    }

    try {
      await db
        .collection("space")
        .doc(currentSpaceId)
        .collection("clients")
        .doc(client.id)
        .update({
          people: fieldValue.arrayUnion(person.uid),
        });

      if (!clientName.trim() && person.userName) {
        setClientName(person.userName);
      }

      setPeople((current) => [...current, person.uid]);
      setLinkPersonIsOpen(false);
    } catch (error) {
      console.error("Link person error:", error);
    }
  };

  const handleUnlinkPerson = async (uid) => {
    if (!client?.id) return;

    try {
      await db
        .collection("space")
        .doc(currentSpaceId)
        .collection("clients")
        .doc(client.id)
        .update({
          people: fieldValue.arrayRemove(uid),
        });

      setPeople((current) => current.filter((id) => id !== uid));
    } catch (error) {
      console.error("Unlink person error:", error);
    }
  };

  const handleHistoryClick = async (date, value) => {
    if (clientmodalMode !== "edit" || !client?.id) return;

    const history = Array.isArray(clientHistory) ? [...clientHistory] : [];

    const index = history.findIndex((item) => item.date === date);

    const checked = Number(value) > 0 ? 0 : 1;

    if (index === -1) {
      history.push({ date, checked });
    } else {
      history[index] = {
        ...history[index],
        checked,
      };
    }

    try {
      await db
        .collection("space")
        .doc(currentSpaceId)
        .collection("clients")
        .doc(client.id)
        .update({ history });

      setClientHistory(history);
    } catch (error) {
      console.error("History update error:", error);
    }
  };

  const clientColorScale = (value) =>
    Number(value) > 0 ? "var(--primary)" : "var(--border2)";

  return createPortal(
    <div className="client">
      <div className="client-backdrop" onClick={handleCloseClient} />

      <div className="client-content">
        <header className="client__header">
          <div className="client__top-row">
            <ItemAddress
              type="client"
              spaceId={targetSpaceId}
              setSpaceId={setTargetSpaceId}
              showProject={false}
              allowQuick={false}
            />

            <div className="client__header-actions">
              <button type="button" onClick={openProjectPicker}>
                <TfiLink />
                <span>Link project</span>
              </button>

              <button
                type="button"
                className="client__exit__button"
                onClick={handleCloseClient}
                aria-label="Close"
              >
                <BsPlusLg />
              </button>
            </div>
          </div>

          {targetSpaceId !== currentSpaceId && (
            <div className="client__move-actions">
              <button type="button" disabled>
                Move here (soon)
              </button>
              <button type="button" disabled>
                Copy here (soon)
              </button>
            </div>
          )}

          <div className="client__identity-row">
            <div className="client__identity-icons">
              <button
                type="button"
                className="client__people-button"
                onClick={openLinkedPeople}
                disabled={!people.length}
                title={
                  people.length
                    ? `${people.length} linked ${
                        people.length === 1 ? "person" : "people"
                      }`
                    : "No linked people"
                }
              >
                {linkedPeople.length ? (
                  <span className="client__avatar-stack">
                    {linkedPeople.slice(0, 3).map((person) => (
                      <span className="client__avatar" key={person.uid}>
                        <Avatar user={person} size={38} />
                      </span>
                    ))}

                    {people.length > 3 && (
                      <span className="client__avatar-more">
                        +{people.length - 3}
                      </span>
                    )}
                  </span>
                ) : (
                  <TbUserPlus />
                )}
              </button>

              <DropdownMenu
                items={platformItems}
                selectedKey={clientPlatform}
                onSelect={setClientPlatform}
                customBtn={
                  <span className="client__platform-trigger-icon">
                    <SelectedPlatformIcon />
                  </span>
                }
                triggerLabel={`Source: ${clientPlatform}`}
                buttonTitle={clientPlatform}
                hasCaret={false}
                align="start"
                minWidth={190}
                maxWidth={240}
                className="client__platform-dropdown"
                triggerClassName="client__platform-trigger"
              />
            </div>

            <div className="client__name-block">
              <input
                autoFocus={clientmodalMode === "add"}
                type="text"
                value={clientName}
                placeholder="Name or company"
                onChange={(event) => setClientName(event.target.value)}
              />

              <button
                type="button"
                className="client__link-person"
                onClick={openPersonPicker}
                disabled={clientmodalMode !== "edit"}
                title={
                  clientmodalMode === "edit"
                    ? "Link a person"
                    : "Save first to link a person"
                }
              >
                <TfiLink />
                {clientmodalMode === "edit"
                  ? "Link person"
                  : "Link person after saving"}
              </button>
            </div>

            <DropdownMenu
              items={TYPE_OPTIONS}
              selectedKey={type}
              onSelect={setType}
              triggerLabel={type}
              buttonTitle="Lead or client"
              align="end"
              minWidth={130}
              maxWidth={160}
              className="client__type-dropdown"
            />
          </div>

          {linkProjectIsOpen && (
            <div className="client__overlay-panel">
              <div className="client__overlay-title">
                <strong>Link project</strong>

                <button
                  type="button"
                  onClick={() => setLinkProjectIsOpen(false)}
                >
                  Close
                </button>
              </div>

              <div className="client__project-picker">
                {availableProjects.map((project) => {
                  const projectId = getProjectId(project);
                  const linked = clientProjects.includes(projectId);

                  return (
                    <button
                      type="button"
                      key={projectId}
                      disabled={linked}
                      onClick={() => handleLinkProject(projectId)}
                    >
                      <span>
                        {project.emojicon} {project.name}
                      </span>

                      <small>{linked ? "Linked" : "Link"}</small>
                    </button>
                  );
                })}

                {!availableProjects.length && <p>No projects available.</p>}
              </div>
            </div>
          )}

          {linkPersonIsOpen && (
            <div className="client__overlay-panel client__people-search">
              <SearchPeople
                embedded
                title={`Link person to ${type.toLowerCase()}`}
                description=""
                disableRowPress
                backButtonAction={() => setLinkPersonIsOpen(false)}
                renderRowAction={({ user }) => (
                  <button
                    type="button"
                    className="client__link-result"
                    onClick={() => handleLinkPerson(user)}
                    disabled={people.includes(user.uid)}
                  >
                    {people.includes(user.uid) ? "Linked" : "Link"}
                  </button>
                )}
              />
            </div>
          )}

          {showLinkedPeople && (
            <div className="client__overlay-panel">
              <div className="client__overlay-title">
                <strong>Linked people</strong>

                <button
                  type="button"
                  onClick={() => setShowLinkedPeople(false)}
                >
                  Close
                </button>
              </div>

              <div className="client__linked-people">
                {linkedPeople.map((person) => (
                  <div key={person.uid}>
                    <span>
                      <Avatar user={person} size={40} />
                      <strong>{person.userName || "User"}</strong>
                    </span>

                    <button
                      type="button"
                      onClick={() => handleUnlinkPerson(person.uid)}
                    >
                      <TfiUnlink />
                      Unlink
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </header>

        <div className="client__scroll">
          <section className="client__section">
            <SectionHeader
              title="Billings"
              open={showBillings}
              onClick={() => setShowBillings((current) => !current)}
            />

            {showBillings &&
              (clientmodalMode === "edit" ? (
                <div className="client__billings-content">
                  <button
                    type="button"
                    className="client__new-invoice-button"
                    onClick={openNewInvoice}
                  >
                    <FiPlus />
                    New invoice
                  </button>

                  <InvoicesList
                    spaceId={currentSpaceId}
                    clientId={client.id}
                    currentUserId={currentUser?.uid}
                    filterByPeriod={false}
                    onOpenInvoice={openExistingInvoice}
                  />
                </div>
              ) : (
                <p className="client__empty-text">
                  You can add billings after adding the client.
                </p>
              ))}
          </section>

          <section className="client__section">
            <SectionHeader
              title="Linked Projects"
              open={showLinkedProjects}
              onClick={() => setShowLinkedProjects((current) => !current)}
            />

            {showLinkedProjects && (
              <div className="client__linked-projects">
                {linkedProjects.map((project) => {
                  const projectId = getProjectId(project);

                  return (
                    <div className="client__project" key={projectId}>
                      <Link to={`e/${projectId}/b`}>
                        <span>{project.emojicon}</span>
                        <strong>{project.name}</strong>
                      </Link>

                      <button
                        type="button"
                        onClick={() => handleUnlinkProject(projectId)}
                      >
                        <TfiUnlink />
                        Unlink
                      </button>
                    </div>
                  );
                })}

                {!linkedProjects.length && (
                  <p className="client__empty-text">No linked projects yet.</p>
                )}
              </div>
            )}
          </section>

          {clientmodalMode === "edit" && (
            <section className="client__section">
              <SectionHeader
                title="Check-In History"
                open={showHistory}
                onClick={() => setShowHistory((current) => !current)}
              />

              {showHistory && (
                <div className="activitycal-container">
                  <InteractiveHeatmap
                    data={clientHistory}
                    onCellPress={handleHistoryClick}
                    colorScale={clientColorScale}
                  />
                </div>
              )}
            </section>
          )}

          <section className="client__section">
            <SectionHeader
              title="Contact Info & Links"
              open={showContactInfo}
              onClick={() => setShowContactInfo((current) => !current)}
            />

            {showContactInfo && (
              <div className="client__contact-form">
                <div className="client__link-input">
                  <input
                    type="url"
                    value={clientSocialLink}
                    placeholder="Social platform link"
                    onChange={(event) =>
                      setClientSocialLink(event.target.value)
                    }
                  />

                  {clientSocialLink && (
                    <a
                      href={externalHref(clientSocialLink)}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Open social link"
                    >
                      <FiExternalLink />
                    </a>
                  )}
                </div>

                <div className="client__field-row">
                  <input
                    type="email"
                    value={clientEmail}
                    placeholder="Email"
                    onChange={(event) => setClientEmail(event.target.value)}
                  />

                  <input
                    type="tel"
                    value={clientPhone}
                    placeholder="Phone"
                    onChange={(event) => setClientPhone(event.target.value)}
                  />
                </div>

                <div className="client__field-row">
                  <div>
                    <label>Website</label>

                    <div className="client__link-input">
                      <input
                        type="url"
                        value={clientWebLink}
                        placeholder="Website"
                        onChange={(event) =>
                          setClientWebLink(event.target.value)
                        }
                      />

                      {clientWebLink && (
                        <a
                          href={externalHref(clientWebLink)}
                          target="_blank"
                          rel="noreferrer"
                          aria-label="Open website"
                        >
                          <FiExternalLink />
                        </a>
                      )}
                    </div>
                  </div>

                  <div>
                    <label>Referred by</label>

                    <input
                      type="text"
                      value={referredBy}
                      placeholder="Referred by"
                      onChange={(event) => setReferredBy(event.target.value)}
                    />
                  </div>
                </div>

                <label>Notes</label>

                <textarea
                  value={clientNotes}
                  placeholder="Notes"
                  onChange={(event) => setClientNotes(event.target.value)}
                />
              </div>
            )}
          </section>
        </div>

        <footer className="client__footer">
          {clientmodalMode === "edit" && (
            <RetroButton onClick={handleDeleteClient}>Delete</RetroButton>
          )}

          <RetroButton onClick={handleSaveClient}>
            {clientmodalMode === "edit" ? `Update ${type}` : `Add ${type}`}
          </RetroButton>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
